import cn from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Column, HeatMapping } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { Neutral800 } from '@src/utils/colors';
import {
  CellDataHeatMapInfo,
  getHeatMap,
  getHeatMapInfo,
  getTextWidth,
  hasNumber,
  TEXT_WIDTH_MARGIN
} from './metrics-table-utils';
import './HeatCell.scss';

// MetricsTable's 'Metrics' variant: 14px text (cell layout in HeatCell.scss), columns capped at
// 136px. The colour fills the row less ROW_PADDING above and below.
const ROW_PADDING = 5;
const FONT_SIZE = 14;
const MAX_WIDTH = 136;
const HEAT_PADDING = 28;
// Replaces the heat outline on cells that carry overlayText, so they stand out before hovering.
const FLAGGED_OUTLINE = `1px dashed ${Neutral800}`;

// The default the Single and Cross Channel preset editors give a heat-mapped column.
export const DEFAULT_HEAT_MAPPING: Partial<HeatMapping> = {
  colorScheme: 'diverging',
  midpoint: 'MIDPOINT',
  stdevClipping: 1
};

export interface HeatColumn {
  colorFor: (value: number) => CellDataHeatMapInfo;
  width: number | undefined;
}

/**
 * Colours and the uniform cell width for one column of values, computed exactly as MetricsTable
 * does (getHeatMap/getHeatMapInfo and its heatMappingWidths). `labels` are the rendered strings.
 */
export const getHeatColumn = (
  values: (number | null | undefined)[],
  labels: string[],
  heatMapping: Partial<HeatMapping> = DEFAULT_HEAT_MAPPING
): HeatColumn => {
  // dataVarName only keys columnMetaDataMap, which is empty here.
  const columns: Column[] = [{ id: 'heat', dataVarName: 'spend', heatMapping }];
  const heatMap = getHeatMap(
    columns,
    values.map(value => [{ label: '', value: value ?? NaN }])
  );
  const cache = new Map<string, number>();
  const width = labels.reduce<number | undefined>((acc, label) => {
    if (!label) return acc;
    const labelWidth =
      Math.ceil(getTextWidth(label, FONT_SIZE, 400, 'Geist', cache)) + TEXT_WIDTH_MARGIN;
    const heatWidth = labelWidth + HEAT_PADDING;
    // As in MetricsTable, the first label sets the width uncapped.
    return acc ? Math.min(MAX_WIDTH, Math.max(acc, heatWidth)) : heatWidth;
  }, undefined);
  return {
    colorFor: value => getHeatMapInfo(0, {}, columns, heatMap, value),
    width
  };
};

interface HeatCellProps {
  label: string;
  value: number | null | undefined;
  column: HeatColumn;
  /** Hover tooltip, as MetricsTable's overlayText; also marks the cell with a dashed outline. */
  overlayText?: string;
}

/** A MetricsTable heat-mapped body cell, for tables that aren't MetricsTable. */
export const HeatCell = ({ label, value, column, overlayText }: HeatCellProps) => {
  const info = hasNumber(value)
    ? column.colorFor(value)
    : { backgroundColor: 'transparent', outline: 'none' };
  const cell = (
    <div className='heat-cell' data-testid={overlayText ? 'flagged-heat-cell' : undefined}>
      <div
        className='cell-wrapper'
        style={{
          backgroundColor: info.backgroundColor,
          height: `calc(100% - ${2 * ROW_PADDING}px)`,
          outline: overlayText ? FLAGGED_OUTLINE : info.outline,
          width: column.width
        }}
      >
        <div
          className={cn('inner-cell', 'metrics-text-regular', 'text', {
            'heat-mapped': info.backgroundColor !== 'transparent' || info.outline !== 'none'
          })}
          style={{ width: column.width }}
        >
          {label}
        </div>
      </div>
    </div>
  );
  if (!overlayText) return cell;
  return (
    <OverlayTrigger placement='top' overlay={<Tooltip>{overlayText}</Tooltip>}>
      {cell}
    </OverlayTrigger>
  );
};
